import React, {useEffect, useMemo, useState} from 'react';
import {CircularProgress, Typography} from '@material-ui/core';
import {Link} from 'react-router-dom';

import {apiGetAllFdApplications, apiGetSupportCustomers} from 'constants/Api';
import {EApplicationStateType} from 'constants/index';
import {IFdApplicationType} from 'constants/interfaces';
import {lshFilterFdApplicationsForLinkedCustomers} from 'services/SupplierCustomerScope';

import './FundingOverviewWidget.scss';

type StatusGroup = {
  key: string;
  label: string;
  states: EApplicationStateType[];
  colour: string;
};

const STATUS_GROUPS: StatusGroup[] = [
  {
    key: 'progress',
    label: 'In progress',
    states: [EApplicationStateType.IN_PROGRESS],
    colour: '#5b8ed9',
  },
  {
    key: 'review',
    label: 'Supplier review',
    states: [
      EApplicationStateType.PENDING_REVIEW,
      EApplicationStateType.IN_REVIEW,
    ],
    colour: '#267373',
  },
  {
    key: 'credit',
    label: 'Credit & terms',
    states: [
      EApplicationStateType.SUBMITTED_TO_CREDIT,
      EApplicationStateType.PENDING_SIGNATURES,
      EApplicationStateType.CONDITION_PRECEDENT,
      EApplicationStateType.TERM_SHEET_SIGNATURE,
    ],
    colour: '#f1a24a',
  },
  {
    key: 'final',
    label: 'Payout / final',
    states: [
      EApplicationStateType.PENDING_PAYOUT,
      EApplicationStateType.FINAL_STAGE,
    ],
    colour: '#67b67a',
  },
  {
    key: 'declined',
    label: 'Declined',
    states: [EApplicationStateType.DECLINED],
    colour: '#e57373',
  },
];

function fundingAskValue(application: IFdApplicationType): number {
  return (
    application.fundingAsk?.fundingRequestCosts?.reduce(
      (total, cost) => total + Number(cost.cost || 0),
      0,
    ) || 0
  );
}

function formatCurrency(value: number): string {
  return `R ${Math.round(value).toLocaleString('en-ZA')}`;
}

function applicationCompletion(application: IFdApplicationType): number {
  return Number(
    application.completedSectionsPercentage ?? application.progress ?? 0,
  );
}

export default function FundingOverviewWidget() {
  const [applications, setApplications] = useState<IFdApplicationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;

    Promise.all([apiGetAllFdApplications(), apiGetSupportCustomers()])
      .then(([response, customers]) => {
        if (alive) {
          setApplications(
            lshFilterFdApplicationsForLinkedCustomers(
              response || [],
              customers,
            ),
          );
        }
      })
      .catch((error) => {
        console.error('Failed to load funding overview widget', error);
        if (alive) setFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const summary = useMemo(() => {
    const totalApplications = applications.length;
    const totalAsk = applications.reduce(
      (total, application) => total + fundingAskValue(application),
      0,
    );
    const averageCompletion = totalApplications
      ? Math.round(
          applications.reduce(
            (total, application) => total + applicationCompletion(application),
            0,
          ) / totalApplications,
        )
      : 0;

    const groups = STATUS_GROUPS.map((group) => {
      const groupApplications = applications.filter((application) =>
        group.states.includes(application.applicationState),
      );
      const value = groupApplications.reduce(
        (total, application) => total + fundingAskValue(application),
        0,
      );

      return {
        ...group,
        count: groupApplications.length,
        value,
        percentage: totalAsk ? Math.round((value / totalAsk) * 100) : 0,
        applicationPercentage: totalApplications
          ? Math.round((groupApplications.length / totalApplications) * 100)
          : 0,
      };
    }).filter((group) => group.count > 0);

    return {averageCompletion, groups, totalApplications, totalAsk};
  }, [applications]);

  const visibleGroups = summary.groups.slice(0, 4);
  const hiddenGroups = summary.groups.slice(4);
  const hiddenGroupCount = hiddenGroups.reduce(
    (total, group) => total + group.count,
    0,
  );

  return (
    <div className="funding-overview-widget">
      <div className="funding-widget-header">
        <Typography className="widget-heading">Funding applications</Typography>
        <Link to="/applications" className="funding-widget-link">
          View all
        </Link>
      </div>

      {loading ? (
        <div className="funding-widget-loading">
          <CircularProgress size={22} thickness={5} />
        </div>
      ) : (
        <>
          <div className="funding-kpis">
            <div className="funding-kpi primary">
              <span className="funding-kpi-value">
                {summary.totalApplications.toLocaleString()}
              </span>
              <span className="funding-kpi-label">Applications</span>
            </div>

            <div className="funding-kpi">
              <span className="funding-kpi-value">
                {formatCurrency(summary.totalAsk)}
              </span>
              <span className="funding-kpi-label">Total ask</span>
            </div>

            <div className="funding-kpi">
              <span className="funding-kpi-value">
                {summary.averageCompletion}%
              </span>
              <span className="funding-kpi-label">Avg complete</span>
            </div>
          </div>

          <div className="funding-breakdown">
            {visibleGroups.length ? (
              <>
                {visibleGroups.map((group) => (
                  <div className="funding-status-row" key={group.key}>
                    <div className="funding-status-main">
                      <span
                        className="funding-status-dot"
                        style={{backgroundColor: group.colour}}
                      />
                      <span className="funding-status-label">
                        {group.label}
                      </span>
                      <span className="funding-status-count">
                        {group.count}
                      </span>
                    </div>
                    <div className="funding-status-meta">
                      <span>{formatCurrency(group.value)}</span>
                      <span>{group.percentage}% of ask</span>
                    </div>
                    <div className="funding-status-track">
                      <span
                        style={{
                          backgroundColor: group.colour,
                          width: `${group.percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}

                {hiddenGroups.length > 0 && (
                  <Link to="/applications" className="funding-more-row">
                    +{hiddenGroupCount} more applications in{' '}
                    {hiddenGroups.length} other statuses
                  </Link>
                )}
              </>
            ) : (
              <Typography className="funding-empty">
                No funding applications found.
              </Typography>
            )}
          </div>

          {failed && (
            <Typography className="funding-error">
              Funding data could not be refreshed.
            </Typography>
          )}
        </>
      )}
    </div>
  );
}
